import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GetStartedModal } from './public/GetStartedModal';
import { hoverScale, getWillChangeStyle } from '../lib/animations/optimized-animations';
import { useTheme } from './ThemeProvider';
import bunkerLogoLight from '../assets/bunker_ai_logo_transparent_light.png';
import bunkerLogoDark from '../assets/bunker_ai_logo_transparent.png';

export function Hero() {
  const { t } = useTranslation('home');
  const navigate = useNavigate();
  const [isGetStartedModalOpen, setIsGetStartedModalOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const { theme } = useTheme();

  // Get the number of slides from translation
  const slides = t('hero.slides', { returnObjects: true }) as Array<{
    badge: string;
    headline: string;
    headlineHighlight: string;
    subheadline: string;
    additionalText: string;
  }>;
  const totalSlides = slides.length;

  // Auto-play functionality
  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 7000); // Change slide every 7 seconds

    return () => clearInterval(timer);
  }, [currentSlide]);

  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToSlide = (index: number) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0,
    }),
  };

  // Check if current slide is bunker.ai slide (now first slide)
  const isBunkerSlide = currentSlide === 0 && slides[0]?.headline?.toLowerCase().includes('bunker');

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Gradient Background - Blue/Purple for light, Orange/Red for dark */}
      <div className="hero-gradient-background"></div>

      {/* Overlay Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300 border border-white/30"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300 border border-white/30"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Content - Carousel */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentSlide}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className="w-full"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, transform: 'scale(0.9) translateZ(0)' }}
              animate={{ opacity: 1, transform: 'scale(1) translateZ(0)' }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white mb-4 border border-white/20"
              style={getWillChangeStyle(['opacity', 'transform'])}
            >
              <span className="text-sm font-medium">{slides[currentSlide].badge}</span>
            </motion.div>

            {/* Bunker.ai Logo - Only show on bunker.ai slide */}
            {isBunkerSlide && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="mb-4"
                style={getWillChangeStyle(['opacity', 'transform'])}
              >
                <img
                  src={theme === 'dark' ? bunkerLogoDark : bunkerLogoLight}
                  alt="bunker.ai"
                  className="h-24 sm:h-32 md:h-40 lg:h-48 mx-auto drop-shadow-2xl"
                />
              </motion.div>
            )}

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.8 }}
              className={`text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight ${isBunkerSlide ? 'mb-2' : 'mb-6'}`}
              style={getWillChangeStyle(['opacity', 'transform'])}
            >
              {slides[currentSlide].headline}
              <br />
              <span className="bg-gradient-to-r from-blue-100 to-purple-100 dark:from-orange-100 dark:to-yellow-100 bg-clip-text text-transparent">
                {slides[currentSlide].headlineHighlight}
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className={`text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed ${isBunkerSlide ? 'mb-4' : 'mb-6'}`}
              style={getWillChangeStyle(['opacity', 'transform'])}
            >
              {slides[currentSlide].subheadline}
            </motion.p>

            {/* Slogan - Only show on bunker.ai slide */}
            {isBunkerSlide && t('hero.slogan') && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.8 }}
                className="text-lg sm:text-xl font-semibold text-white max-w-2xl mx-auto mb-4"
                style={getWillChangeStyle(['opacity', 'transform'])}
              >
                {t('hero.slogan')}
              </motion.p>
            )}

            {/* Claim - Only show on bunker.ai slide */}
            {isBunkerSlide && t('hero.claim', { defaultValue: '' }) && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-100 to-purple-100 dark:from-orange-200 dark:to-yellow-200 bg-clip-text text-transparent max-w-2xl mx-auto mb-6"
                style={getWillChangeStyle(['opacity', 'transform'])}
              >
                {t('hero.claim')}
              </motion.p>
            )}

            {/* Additional Text */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className={`text-base sm:text-lg text-white/80 max-w-2xl mx-auto ${isBunkerSlide ? 'mb-8' : 'mb-12'}`}
              style={getWillChangeStyle(['opacity', 'transform'])}
            >
              {slides[currentSlide].additionalText}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <motion.button
                {...hoverScale}
                onClick={() => setIsGetStartedModalOpen(true)}
                className="group px-8 py-4 bg-white text-black rounded-lg font-semibold text-lg shadow-2xl hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                style={getWillChangeStyle(['transform'])}
              >
                {t('hero.getStarted')}
                <ArrowRight className="w-5 h-5 text-black group-hover:translate-x-1 transition-transform" />
              </motion.button>

              <motion.button
                {...hoverScale}
                onClick={() => {
                  const currentLang = localStorage.getItem('i18nextLng') || 'de';
                  navigate(`/${currentLang}/bunker`);
                }}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold text-lg border-2 border-white/30 hover:bg-white/20 transition-all duration-300"
                style={getWillChangeStyle(['transform'])}
              >
                {t('hero.learnMore')}
              </motion.button>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* Slide Indicators */}
        <div className="flex justify-center gap-2 mt-12">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'w-8 bg-white'
                  : 'w-2 bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2"
        >
          <motion.div
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-1 h-2 bg-white rounded-full"
          />
        </motion.div>
      </motion.div>

      {/* Get Started Modal */}
      <GetStartedModal
        open={isGetStartedModalOpen}
        onOpenChange={setIsGetStartedModalOpen}
      />
    </section>
  )
}
